// WHAT: Component displaying list of SwitchBot hub status indicators
// WHY:  Provides quick visual feedback on hub connectivity status
// DOMAIN: SmartHome → SwitchBot
// DEPENDENCIES: SwitchBotDevice type from @tracq/frontend-smarthome/services
// RISKS:
//   - NOTE: Fixed positioning (bottom-26 left-1) may overlap with other UI elements
//   - NOTE: Always shows "Online" - no actual connectivity check
//   - NOTE: Uses index as key - may cause issues if hubs array changes

import { type SwitchBotDevice } from "@/domains/smarthome/services";

/**
 * Props for HubStatusList component
 */
interface HubStatusListProperties {
  /** Array of SwitchBot hub devices to display */
  readonly hubs: readonly SwitchBotDevice[];
}

interface HubRenderData {
  deviceId: string;
  deviceName: string;
}

const mapToHubRenderData = (hubs: SwitchBotDevice[]): HubRenderData[] =>
  hubs
    .map((hub): HubRenderData | null => {
      const id = (hub as { deviceId?: unknown }).deviceId;
      const name = (hub as { deviceName?: unknown }).deviceName;

      if (typeof id !== "string" || typeof name !== "string") {
        return null;
      }

      return {
        deviceId: id,
        deviceName: name,
      };
    })
    .filter((hub): hub is HubRenderData => hub !== null);

/**
 * WHAT:
 *   Component that displays a fixed-position list of SwitchBot hub status indicators
 *   showing hub names and online status.
 *
 * WHY:
 *   Provides quick visual feedback on hub connectivity without taking up main screen space.
 *
 * PROPS:
 *   - hubs: SwitchBotDevice[] — Array of hub devices
 *
 * BEHAVIOR:
 *   - Returns null if no hubs provided
 *   - Displays fixed-position overlay at bottom-left
 *   - Shows hub name and "Online" status with animated pulse indicator
 *   - Uses backdrop blur for better visibility
 *
 * RISKS:
 *   - NOTE: Fixed positioning may overlap with other UI elements
 *   - NOTE: Always shows "Online" - no actual connectivity verification
 *   - NOTE: Uses index as key - should use unique identifier
 *   - NOTE: Position may not be responsive on small screens
 *
 * @param root0
 *
 * @param root0.hubs
 *
 * @returns {JSX.Element | null} Hub status list component or null if no hubs
 *
 */
export function HubStatusList({ hubs }: HubStatusListProperties) {
  // NOTE: Return null if no hubs to display
  if (hubs.length === 0) {
    return null;
  }

  const hubsToRender = mapToHubRenderData([...hubs]);

  return (
    <div className="fixed bottom-26 left-1 bg-slate-800/90 backdrop-blur-sm rounded-lg p-3 border border-slate-700 shadow-lg">
      <div className="text-xs text-slate-400 mb-2">Hub Status</div>
      <div className="space-y-1">
        {hubsToRender.map((hub) => (
          <div className="flex items-center space-x-2" key={hub.deviceId}>
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-xs text-slate-300">{hub.deviceName}</span>
            <span className="text-xs text-slate-500">•</span>
            <span className="text-xs text-green-400">Online</span>
          </div>
        ))}
      </div>
    </div>
  );
}
